/**
 * Execution-Source chip — small header pill users can click to switch scope.
 *
 * Auto-follows the connected broker unless the user has explicitly picked
 * a source. A mismatch between the current scope and the connected broker
 * surfaces a subtle warning (analytics will be empty for that scope).
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import {
  EXECUTION_SOURCES, useExecutionSource,
  brokerModeToSource, BROKER_CHANGED_EVENT,
  isExecutionSourceExplicit, resetExecutionSourceOverride,
  setExecutionSource,
} from "@/lib/executionSource";

export default function ExecutionSourceChip({ compact = false }) {
  const [source, setSource] = useExecutionSource();
  const [brokerMode, setBrokerMode] = useState("disconnected");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOff = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("click", onOff);
    return () => window.removeEventListener("click", onOff);
  }, []);

  useEffect(() => {
    api.get("/broker/status").then(r => setBrokerMode(r.data.mode)).catch(() => {});
    const handler = (e) => {
      if (e.detail?.mode) setBrokerMode(e.detail.mode);
      else api.get("/broker/status").then(r => setBrokerMode(r.data.mode)).catch(() => {});
    };
    window.addEventListener(BROKER_CHANGED_EVENT, handler);
    return () => window.removeEventListener(BROKER_CHANGED_EVENT, handler);
  }, []);

  const active = EXECUTION_SOURCES.find(s => s.id === source) || EXECUTION_SOURCES[2];
  const expected = brokerModeToSource(brokerMode);
  // Mismatch = user has picked a source that doesn't match the connected
  // broker (e.g. ALPACA_PAPER selected but broker is mock).  We show a
  // warning triangle + a "Follow broker" quick-reset in the menu.
  const isMismatch = expected !== "ALL"
                        && source !== "ALL"
                        && source !== expected
                        && isExecutionSourceExplicit();

  const followBroker = (e) => {
    e.stopPropagation();
    resetExecutionSourceOverride();
    setExecutionSource(expected, { explicit: false });
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="execution-source-chip">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className={`chip cursor-pointer flex items-center gap-1.5 ${
          active.id === "ALL" ? "text-signal-yellow border-current"
          : isMismatch ? "text-signal-yellow border-current"
          : "text-muted-foreground border-border/60 hover:text-foreground"}`}
        data-testid="execution-source-toggle"
        title={isMismatch
          ? `Scope is ${active.label} but the connected broker is ${brokerMode.replace("_"," ")}. Analytics will be empty.`
          : `Applies to every analytics view + CSV export`}
      >
        <span>{active.dot}</span>
        {!compact && <span className="mono uppercase tracking-wider text-[10px]">{active.label}</span>}
        {isMismatch && <AlertTriangle size={10} className="text-signal-yellow" data-testid="execution-source-mismatch"/>}
        <ChevronDown size={10}/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-background border border-border/60 min-w-[220px]" data-testid="execution-source-menu">
          {EXECUTION_SOURCES.map(s => (
            <button
              key={s.id}
              onClick={(e) => { e.stopPropagation(); setSource(s.id); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs mono uppercase tracking-wider flex items-center gap-2 hover:bg-secondary/40 ${source === s.id ? "text-foreground bg-secondary/30" : "text-muted-foreground"}`}
              data-testid={`execution-source-option-${s.id}`}
            >
              <span>{s.dot}</span>
              <span>{s.label}</span>
              {s.id === expected && expected !== "ALL" && (
                <span className="ml-auto text-[9px] text-muted-foreground italic normal-case">connected</span>
              )}
              {s.id === "ALL" && <AlertTriangle size={11} className="text-signal-yellow ml-auto"/>}
            </button>
          ))}
          {isMismatch && (
            <button
              onClick={followBroker}
              className="w-full text-left px-3 py-1.5 text-[10px] mono uppercase tracking-widest border-t border-border/40 text-signal-yellow hover:bg-signal-yellow/10"
              data-testid="execution-source-follow-broker">
              ⤾ Follow broker ({expected.replace("_"," ").toLowerCase()})
            </button>
          )}
          <div className="border-t border-border/40 px-3 py-1.5 text-[10px] text-muted-foreground">
            {brokerMode === "disconnected"
              ? "No broker connected. Applies to every analytics view + CSV export."
              : <>Auto-follows the <span className="mono uppercase">{brokerMode.replace("_"," ")}</span> connection.</>}
          </div>
        </div>
      )}
    </div>
  );
}

export function ExecutionSourceBanner() {
  const [source] = useExecutionSource();
  const [bothActive, setBothActive] = useState(false);
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const r = await api.get("/broker/ledger/execution-stats");
        if (alive) {
          const s = r?.data?.counts_by_source || {};
          setBothActive((s.INTERNAL_PAPER_MOCK || 0) > 0 && (s.ALPACA_PAPER || 0) > 0);
        }
      } catch { if (alive) setBothActive(false); }
    };
    refresh();
    // Re-check on broker-changed, window focus, and every 60s so the banner
    // reacts to executions arriving after mount.
    const onFocus = () => refresh();
    const onBrokerChanged = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("tm:broker-changed", onBrokerChanged);
    const t = setInterval(refresh, 60000);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("tm:broker-changed", onBrokerChanged);
      clearInterval(t);
    };
  }, [source]);
  if (source !== "ALL" || !bothActive) return null;
  return (
    <div className="border border-signal-yellow/40 bg-signal-yellow/5 text-signal-yellow px-3 py-2 text-xs flex items-center gap-2" data-testid="execution-source-banner">
      <AlertTriangle size={12}/>
      This view combines Alpaca broker-simulated results with Internal Paper mock results.
    </div>
  );
}
